import React, { useEffect, useState } from 'react';
import IFeature from "../../../types/feature";
import Button from "../elements/Button";
import { getSortingSteps } from "../../../utils/content";
import { useAppDispatch, useAppSelector } from "../../../store/hooks";
import { featureAPI } from "../../../api/feature";
import ICommonOptions from "../../../types/commonOptions";
import { setOptionsAction } from "../../../store/reducers/commonSlice";
import ArrowDownIcon from "../icons/ArrowDownIcon";
import DownloadIcon from "../icons/DownloadIcon";
import endpoints from "../../../endpoints";

export const inputClassName = 'block w-full flex-1 rounded-none rounded-r-sm border border-gray-300 focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm px-3 py-1 rounded-l-md'
export const labelClassName = 'inline-flex items-center rounded-l-sm border border-r-0 border-gray-300 bg-gray-50 px-3 text-sm text-gray-500 w-[50%]'

const fields: { [key: string]: { title: string, type: string } } = {
    limit: {
        title: 'Pages limit',
        type: 'number'
    },
    authors: {
        title: 'Authors limit',
        type: 'text'
    },
    blacklist_keywords: {
        title: 'Blacklist keywords',
        type: 'file'
    },
    whitelist_keywords: {
        title: 'Whitelist keywords',
        type: 'file'
    },
    templates: {
        title: 'Templates',
        type: 'file'
    },
    ai_requests_limit: {
        title: 'AI requests limit',
        type: 'number'
    },
    chatgpt_request: {
        title: 'ChatGPT request',
        type: 'area'
    },
    chatgpt_model: {
        title: 'ChatGpt model',
        type: 'text'
    }
}

interface IAdminFeaturesState {
    [key: string]: any
}

const OptionsForm = ({ onClose }: { onClose: () => void }) => {
    const dispatch = useAppDispatch()

    const commonOptions = useAppSelector(state => state.common.options)
    const authUser = useAppSelector(state => state.auth.authUser)

    const [getAdminFeatures, { data: adminFeatures, isLoading: isAdminFeaturesLoading, isSuccess: isAdminFeaturesSuccess }] = featureAPI.useGetAllMutation()
    const [updateAdminFeatures] = featureAPI.useUpdateMutation()

    const [featuresState, setFeaturesState] = useState<Pick<ICommonOptions, 'revisionsStep'>>({
        revisionsStep: commonOptions.revisionsStep
    })

    const [adminFeaturesState, setAdminFeaturesState] = useState<IAdminFeaturesState>({})

    const changeFeaturesState = (field: keyof ICommonOptions, value: any) => {
        if (value === undefined) return;
        setFeaturesState({
            ...featuresState,
            [field]: value
        })
    }

    const changeAdminFeaturesState = (name: string, value: string | number | File) => {
        setAdminFeaturesState({
            ...adminFeaturesState,
            [name]: value
        })
    }

    const saveOptions = () => {
        updateAdminFeatures(adminFeaturesState)
        dispatch(setOptionsAction(featuresState))
        onClose()
    }

    useEffect(() => {
        authUser && authUser.isAdmin && getAdminFeatures()
    }, [])

    useEffect(() => {
        if (adminFeatures) {
            setAdminFeaturesState(adminFeatures)
        }
    }, [adminFeatures])

    return (
        <form className="space-y-6" action="#">
            <div className="mb-4 flex flex-col items-center">
                {authUser?.isAdmin && Object.keys(adminFeaturesState).map((featureName: string) =>
                    <Field
                        key={featureName}
                        type={fields[featureName].type}
                        name={featureName}
                        value={adminFeaturesState[featureName]}
                        onChange={changeAdminFeaturesState}
                    />)
                }
            </div>
            <div className="">
                <Button
                    type="button"
                    label="Save"
                    onClick={saveOptions}
                />
            </div>
        </form>
    );
};

type TFieldProps = {
    type: string,
    name: string,
    value: any,
    onChange: (name: string, value: any) => void
}

const Field = ({ type, name, value, onChange }: TFieldProps) => {
    const [download, { }] = featureAPI.useDownloadMutation()

    if (type === 'number') {
        return <div className="w-full mb-1">
            <div className="flex rounded-md shadow-sm w-full">
                <label className={labelClassName}>{fields[name].title}</label>
                <input
                    type="number"
                    className="py-2 px-4 border-[2px] outline-none w-[50%]"
                    onChange={(e) => onChange(name, e.target.value)}
                    value={value}
                />
            </div>
        </div>
    }

    if (type === 'text') {
        return <div className="w-full mb-1">
            <div className="flex rounded-md shadow-sm w-full">
                <label className={labelClassName}>{fields[name].title}</label>
                <input
                    type="text"
                    className="py-2 px-4 border-[2px] outline-none w-[50%]"
                    onChange={(e) => onChange(name, e.target.value)}
                    value={value}
                />
            </div>
        </div>
    }

    if (type === 'area') {
        return <div className="w-full mb-1">
            <div className="flex rounded-md shadow-sm w-full">
                <label className={labelClassName}>{fields[name].title}</label>
                <textarea
                    className="py-2 px-4 border-[2px] outline-none w-[50%]"
                    onChange={(e) => onChange(name, e.target.value)}
                    value={value}
                    rows={5}
                />
            </div>
        </div>
    }

    if (type === 'file') {
        return <div className="w-full mb-1">
            <div className="flex rounded-md shadow-sm w-full">
                <label className={labelClassName}>{fields[name].title}</label>
                <input
                    type="file"
                    className="py-2 px-4 border-[2px] outline-none w-[40%]"
                    onChange={(e) => e.target.files && onChange(name, e.target.files[0])}
                    accept=".csv"
                />
                <span
                    className="flex items-center cursor-pointer w-[10%] justify-center"
                    title={value}
                    onClick={typeof value === 'string' ? () => download(value) : () => { }}
                >
                    <DownloadIcon />
                </span>
            </div>
        </div>
    }

    return <></>
}


export default OptionsForm;